import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, FolderKanban, User as UserIcon } from "lucide-react";
import { portalApi } from "../../context/PortalAuthContext";
import { usePortalNotifications } from "../../context/PortalNotificationsContext";
import { PRIORITY_STYLES, priorityLabel } from "../processes/processConstants";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import TimelineList from "../processes/TimelineList";
import PortalWorkflowTracker from "./PortalWorkflowTracker";
import { PortalDocumentCard, PortalDocumentViewer } from "./PortalDocumentComponents";
import PortalProcessDocuments from "./PortalProcessDocuments";
import { usePortalSettings } from "./PortalSettingsContext";
import { usePortalI18n } from "./PortalI18nContext";
import { fmtCurrency } from "./PortalReceiptComponents";
import { resolveProcessStatus, PORTAL_STAGE_KEYS } from "./portalProcessStatus";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric" }); }
  catch { return iso; }
};

// AP-167 · Mensagem contextual — fonte única: resolveProcessStatus(proc, t).
// Garante que Badge, Tracker, Mensagem e Histórico mostrem o MESMO status.
function StageMessage({ resolved }) {
  if (!resolved?.message) return null;
  return (
    <div data-testid="portal-stage-message"
         className={`rounded-2xl border p-4 flex items-start gap-3 ${resolved.tone.card}`}>
      <div className="text-lg leading-none">{resolved.tone.icon}</div>
      <p className="text-sm font-medium">{resolved.message}</p>
    </div>
  );
}

export default function PortalProcessDetail() {
  const { pid } = useParams();
  const navigate = useNavigate();
  const { t } = usePortalI18n();
  // AP-103 FINAL · Flags do tenant.
  const { settings } = usePortalSettings();
  const canShowMp =
    settings?.portal_payments_enabled !== false &&
    settings?.portal_show_mp_button !== false;
  const showAmounts = settings?.portal_show_amounts !== false;

  // AP-167 · Marcar notificações do processo como lidas (sincroniza o sino).
  const { markReadByProcess } = usePortalNotifications();

  const [proc, setProc] = useState(null);
  const [tl, setTl] = useState([]);
  const [docs, setDocs] = useState([]);
  const [viewerDoc, setViewerDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const [p, tl_, d] = await Promise.all([
          portalApi.get(`/portal/processes/${pid}`),
          portalApi.get(`/portal/processes/${pid}/timeline`),
          portalApi.get(`/portal/documents?process_id=${encodeURIComponent(pid)}`),
        ]);
        if (!alive) return;
        setProc(p.data);
        setTl(tl_.data.items || []);
        setDocs(d.data.items || []);
        // AP-167 · Marca as notificações deste processo como lidas (badge diminui).
        try { await markReadByProcess(pid); } catch { /* silent */ }
      } catch (e) {
        if (alive) setError(e?.response?.data?.detail || t("detail.loadError"));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pid]);

  if (loading) {
    return <div data-testid="portal-process-detail-loading" className="text-center py-16 text-sm text-muted-foreground">{t("common.loading")}</div>;
  }
  if (error || !proc) {
    return (
      <div data-testid="portal-process-detail-error" className="rounded-2xl border border-border bg-white px-8 py-12 text-center">
        <p className="text-sm text-muted-foreground">{error || t("detail.notFound")}</p>
        <Button variant="outline" size="sm" className="mt-4" onClick={() => navigate("/portal/processes")}>
          <ArrowLeft className="h-4 w-4 mr-1" /> {t("detail.back")}
        </Button>
      </div>
    );
  }

  // AP-167 · Fonte única da verdade — mesmo status em TODA a tela.
  const resolved = resolveProcessStatus(proc, t);
  const pr = PRIORITY_STYLES[proc.priority] || PRIORITY_STYLES.medium;
  // Mesma regra do PortalWorkflowTracker: concluída = etapa anterior à azul.
  const lastCompletedKey = PORTAL_STAGE_KEYS[PORTAL_STAGE_KEYS.indexOf(resolved.stageKey) - 1];

  return (
    <div data-testid="portal-process-detail" className="space-y-6">
      {/* Header */}
      <div className="rounded-2xl border border-border bg-white p-5 flex flex-col md:flex-row md:items-center gap-4">
        <Button variant="outline" size="sm" onClick={() => navigate("/portal/processes")}
                data-testid="portal-process-detail-back">
          <ArrowLeft className="h-4 w-4 mr-1" /> {t("detail.back")}
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono text-xs text-muted-foreground" data-testid="portal-process-number">
              {proc.process_number}
            </span>
            <Badge className={`border ${resolved.tone.bg}`} variant="outline" data-testid="portal-process-status-badge">
              <span className={`inline-block h-2 w-2 rounded-full mr-1.5 ${resolved.tone.dot}`} />
              {resolved.label}
            </Badge>
            <Badge className={`border ${pr.bg}`} variant="outline">{priorityLabel(proc.priority)}</Badge>
          </div>
          <h2 className="mt-1 text-xl font-display font-bold text-foreground">
            {proc.service_name || t("detail.fieldService")}
          </h2>
          <p className="text-xs text-muted-foreground">{t("detail.openedAt")} {fmtDate(proc.opening_date)} · {t("detail.lastUpdate")} {fmtDate(proc.last_update)}</p>
        </div>
      </div>

      {/* Info grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="rounded-2xl border border-border bg-white p-5">
          <div className="flex items-center gap-2 mb-3">
            <UserIcon className="h-4 w-4 text-primary" />
            <h3 className="text-sm font-semibold">{t("detail.clientInfo")}</h3>
          </div>
          <dl className="text-sm space-y-1.5">
            <div className="flex justify-between gap-3"><dt className="text-muted-foreground">{t("detail.fieldName")}</dt><dd className="font-medium text-right">{proc.client_name || "—"}</dd></div>
            <div className="flex justify-between gap-3"><dt className="text-muted-foreground">{t("detail.fieldBirthDate")}</dt><dd className="text-right">{proc.client_birth_date || "—"}</dd></div>
            <div className="flex justify-between gap-3"><dt className="text-muted-foreground">{t("detail.fieldWhatsapp")}</dt><dd className="text-right">{proc.client_whatsapp || "—"}</dd></div>
            {proc.client_email && <div className="flex justify-between gap-3"><dt className="text-muted-foreground">{t("detail.fieldEmail")}</dt><dd className="text-right truncate">{proc.client_email}</dd></div>}
            {proc.client_document && <div className="flex justify-between gap-3"><dt className="text-muted-foreground">{t("detail.fieldDocument")}</dt><dd className="text-right">{proc.client_document}</dd></div>}
            {proc.client_nationality && <div className="flex justify-between gap-3"><dt className="text-muted-foreground">{t("detail.fieldNationality")}</dt><dd className="text-right">{proc.client_nationality}</dd></div>}
          </dl>
        </div>

        <div className="rounded-2xl border border-border bg-white p-5">
          <div className="flex items-center gap-2 mb-3">
            <FolderKanban className="h-4 w-4 text-primary" />
            <h3 className="text-sm font-semibold">{t("detail.processInfo")}</h3>
          </div>
          <dl className="text-sm space-y-1.5">
            <div className="flex justify-between gap-3"><dt className="text-muted-foreground">{t("detail.fieldService")}</dt><dd className="text-right">{proc.service_name || "—"}</dd></div>
            <div className="flex justify-between gap-3"><dt className="text-muted-foreground">{t("detail.fieldResponsible")}</dt><dd className="text-right">{proc.responsible_name || "—"}</dd></div>
            <div className="flex justify-between gap-3"><dt className="text-muted-foreground">{t("detail.fieldCurrentStatus")}</dt>
              <dd className="text-right" data-testid="portal-process-status-text">{resolved.label}</dd>
            </div>
            <div className="flex justify-between gap-3"><dt className="text-muted-foreground">{t("detail.fieldLastCompleted")}</dt>
              <dd className="text-right text-emerald-700 font-medium" data-testid="portal-process-last-completed">{lastCompletedKey ? t(`stage.${lastCompletedKey}.label`) : "—"}</dd>
            </div>
            <div className="flex justify-between gap-3"><dt className="text-muted-foreground">{t("detail.fieldInProgress")}</dt>
              <dd className="text-right text-blue-700 font-medium" data-testid="portal-process-in-progress">{t(`stage.${resolved.stageKey}.label`)}</dd>
            </div>
            <div className="flex justify-between gap-3"><dt className="text-muted-foreground">{t("detail.lastUpdate")}</dt><dd className="text-right">{fmtDate(proc.last_update)}</dd></div>
          </dl>
          {proc.notes && (
            <div className="mt-3 pt-3 border-t border-border">
              <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">{t("detail.notes")}</p>
              <p className="text-sm whitespace-pre-wrap">{proc.notes}</p>
            </div>
          )}
        </div>
      </div>

      {/* AP-167 · Workflow tracker localizado */}
      <PortalWorkflowTracker value={resolved.stageKey} />

      {/* AP-167 · Mensagem contextual (fonte única: resolveProcessStatus) */}
      <StageMessage resolved={resolved} />

      {/* AP-106 · Seção Documentação — checklist do processo com upload direto */}
      <PortalProcessDocuments processId={proc.id} />

      {/* AP-103 FINAL · Seção de pagamento */}
      {canShowMp && (
        <div className="rounded-2xl border border-border bg-white p-5 space-y-3" data-testid="portal-process-payment-section">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <h3 className="text-sm font-semibold">{t("detail.payments")}</h3>
            {showAmounts && proc.total_paid > 0 && (
              <span className="text-[11px] text-muted-foreground" data-testid="portal-process-paid-total">
                {t("detail.alreadyPaid")}: <b className="tabular-nums">{fmtCurrency(proc.total_paid, "BRL")}</b>
              </span>
            )}
          </div>
          {proc.has_pending_payment ? (
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                {showAmounts ? (
                  <p className="text-sm">
                    {t("detail.outstanding")}:{" "}
                    <b className="tabular-nums text-foreground" data-testid="portal-process-outstanding">
                      {fmtCurrency(proc.outstanding_balance, "BRL")}
                    </b>
                  </p>
                ) : (
                  <p className="text-sm text-muted-foreground italic" data-testid="portal-process-outstanding-hidden">
                    {t("detail.outstandingHidden")}
                  </p>
                )}
              </div>
              <button
                type="button"
                data-testid="portal-process-pay-mp"
                onClick={async () => {
                  const amount = Number(proc.outstanding_balance || 0);
                  if (!(amount > 0)) return;
                  try {
                    const { data } = await portalApi.post(
                      "/portal/payments/mercadopago/create-preference",
                      { process_id: proc.id, amount, description: proc.service_name }
                    );
                    if (data?.init_point) window.location.href = data.init_point;
                  } catch (e) {
                    alert(e?.response?.data?.detail || t("detail.payErrorStart"));
                  }
                }}
                className="inline-flex items-center gap-2 rounded-full bg-[#009EE3] text-white font-semibold text-sm px-4 py-2 shadow-sm hover:shadow-md hover:-translate-y-0.5 active:scale-[0.98] transition-all"
              >
                💳 {t("detail.payMp")}
              </button>
            </div>
          ) : (
            <p className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2"
               data-testid="portal-process-no-pending">
              ✅ {t("detail.noPending")}
            </p>
          )}
        </div>
      )}

      {/* Timeline */}
      <div className="rounded-2xl border border-border bg-white p-5" data-testid="portal-process-timeline">
        <h3 className="text-sm font-semibold mb-3">{t("detail.timeline")}</h3>
        <TimelineList
          events={tl}
          byKey={(k) => ({ key: k, label: t(`stage.${k}.label`) !== `stage.${k}.label` ? t(`stage.${k}.label`) : k, color: "slate" })}
          emptyLabel={t("detail.timelineEmpty")}
        />
      </div>

      {/* Documents */}
      <div className="rounded-2xl border border-border bg-white p-5" data-testid="portal-process-documents">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold">{t("detail.docsHere")}</h3>
          <Badge variant="outline" className="text-[10px]" data-testid="portal-process-doc-count">
            {docs.length} {docs.length === 1 ? t("detail.docCountOne") : t("detail.docCountMany")}
          </Badge>
        </div>
        {docs.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t("detail.docEmpty")}</p>
        ) : (
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
            {docs.map((d) => (
              <PortalDocumentCard key={d.id} doc={d} onView={setViewerDoc} />
            ))}
          </div>
        )}
      </div>

      <PortalDocumentViewer
        doc={viewerDoc}
        open={!!viewerDoc}
        onClose={() => setViewerDoc(null)}
      />
    </div>
  );
}
